import React from 'react';

import { useNavigate } from 'react-router-dom'; // Import the useNavigate hook

import {
    AdminPanelSettings,
    Search,
    SettingsSuggest,
    Apps,
    PlaylistAddCheck,
    LocalShipping
} from '@mui/icons-material';

import Container from './blocks/container';

import { useAuth } from '../context/auth-context';

const HomeNavigator = ({
    setIsSearchModalOpen
}) => {

    // State to manage modal visibility

    // Get the current user from the auth context
    const { currentUser } = useAuth();

    // Initialize the navigate hook
    const navigate = useNavigate();

    /**
     * hasAdminAccess
     * Function to check if the current user has admin access.
     * It checks if the user has the 'ROLE_ADMIN_INTERFACE' role.
     * @returns {boolean} - Returns true if the user has admin access, false otherwise
     */
    function hasAdminAccess() {
        return (
            Array.isArray(currentUser?.roles) &&
            currentUser.roles.some(r => r.role === 'ROLE_ADMIN_INTERFACE')
        );
    }

    /**
     * hasPartOrdersAccess
     * Function to check if the current user has access to part orders.
     * It checks if the user has the 'ROLE_PART_ORDERS_INTERFACE' role.
     * @returns {boolean} - Returns true if the user has part orders access, false otherwise
     */
    function hasPartOrdersAccess() {
        return (
            Array.isArray(currentUser?.roles) &&
            currentUser.roles.some(r => r.role === 'ROLE_PART_ORDERS_INTERFACE')
        );
    }

    /**
     * handleSearchIntervention
     * Function to handle button click and open the search modal.
     * This function sets the isSearchModalOpen state to true.
     * This will open the search modal.
     * @returns {void}
     */
    const handleSearchIntervention = () => {
        setIsSearchModalOpen(true);
    };

    /**
     * handleGalleryAccess
     * Function to handle button click and navigate to the gallery page.
     */
    const handleGalleryAccess = () => {
        navigate('/internal-gallery');
    }

    /**
     * handleParametersAccess
     * Function to handle button click and navigate to the parameters page.
     */
    const handleParametersAccess = () => {
        navigate('/settings');
    }

    /**
     * handleAdminAccess
     * Function to handle button click and navigate to the admin page.
     */
    const handleAdminAccess = () => {
        navigate('/admin');
    }

    /**
     * handlePartRequestsAccess
     * Function to handle button click and navigate to the part requests page.
     */
    const handlePartRequestsAccess = () => {
        navigate('/parts-requests');
    }

    /**
     * handlePartOrdersAccess
     * Function to handle button click and navigate to the part orders page.
     * This function checks if the user has access to part orders before navigating.
     * @returns {void}
     */
    const handlePartOrdersAccess = () => {
        navigate('/parts-orders');
    }

    // Get the last appointment from the list
    return (
        <React.Fragment>
            <Container
                className="home-buttons"
                title="Tableau de bord"
                titleBackground='var(--primary-color)'
            >
                <button 
                    className="search-intervention-btn"
                    onClick={handleSearchIntervention}
                    aria-label="Rechercher un dossier"
                >
                    <Search />
                    Rechercher un dossier
                </button>
                <button
                    className="parameters-btn"
                    onClick={handleParametersAccess}
                    aria-label="Paramètres de l'application"
                >
                    <SettingsSuggest />
                    Paramètres de l'application
                </button>
                <button
                    className="internal-gallery-btn"
                    onClick={handleGalleryAccess}
                    aria-label="Galerie interne de l'application"
                >
                    <Apps />
                    Galerie interne
                </button>
                <button
                    className="part-orders-btn"
                    onClick={handlePartRequestsAccess}
                    aria-label="Accéder aux demandes de pièces"
                >
                    <PlaylistAddCheck />
                    Demandes de pièces
                </button>
                { hasAdminAccess() && (
                    <button
                        className="admin-access-btn"
                        onClick={handleAdminAccess}
                        aria-label="Accéder à l'interface d'administration"
                    >
                        <AdminPanelSettings />
                        Accéder à l'interface d'administration
                    </button>
                )}
                { hasPartOrdersAccess() && (
                    <button
                        className="part-orders-adm-btn"
                        onClick={handlePartOrdersAccess}
                        aria-label="Accéder aux commandes de pièces"
                    >
                        <LocalShipping />
                        Commandes de pièces
                    </button>
                )}
            </Container>

        </React.Fragment>
    );
};

export default HomeNavigator;